import { inject, Injectable } from '@angular/core';
import { FeatureFlagsService } from '@services/feature-flags.service';
import { SettingsService } from '@services/settings.service';
import { SettingsStore } from '@services/settings.store';
import { UserService } from '@services/user.service';
import { Column, DataViewItemWithAccess, Filter, PageConfig, View } from '@shared/components/data-view/data-view-types';
import { UserRole } from '@shared/models/user-role.model';
import { IUser } from '@shared/models/user.model';
import { difference, intersection } from 'lodash';
import { forkJoin, from, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

const CURRENT_DATAVIEW_VERSION = '3.0.0';
const DATAVIEW_SETTINGS_KEY = 'dataview3';
const DATAVIEW_COLUMN_SETTINGS_KEY = 'dataview3-1';
const DEFAULT_MAP_CONFIG = {
  displayedInfo: {
    basicInfo: true,
    enrollments: true,
    flags: false,
    scorecard: false,
  },
};

@Injectable({ providedIn: 'root' })
export class DataViewConfigService {
  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly settingsService = inject(SettingsService);
  private readonly settingsStore = inject(SettingsStore);
  private readonly userService = inject(UserService);

  getPageAndDomainDefinitions(page: string): Observable<{
    pageResult: Column.DataViewColumnGallerySchema | undefined;
    domainResult: Column.DataViewDomainColumnsSchema | undefined;
  }> {
    return this.settingsStore
      .getSettings<Column.DataViewColumnGallerySchema>(DATAVIEW_SETTINGS_KEY, page)
      .pipe(
        mergeMap(pageResult =>
          this.settingsStore
            .getSettings<Column.DataViewDomainColumnsSchema>(DATAVIEW_COLUMN_SETTINGS_KEY, `${pageResult?.domain}_columns`)
            .pipe(map(domainResult => ({ pageResult, domainResult }))),
        ),
      );
  }

  private mapAvailableColumns(
    pageResult: Column.DataViewColumnGallerySchema | undefined,
    domainResult: Column.DataViewDomainColumnsSchema | undefined,
    currentUser?: IUser,
  ): Column.DataViewColumn[] {
    const columns: Column.DataViewColumnGalleryColumn[] = [];
    for (const [id, domainColumnDefinition] of Object.entries(domainResult?.columns ?? {})) {
      const pageColumnDefinition = pageResult?.columns.find(column => column.id === id);
      if (pageColumnDefinition && !pageColumnDefinition.filterOnly) {
        columns.push({
          ...domainColumnDefinition,
          ...pageColumnDefinition,
          id,
        });
      }
    }
    return this.mapAvailableItems(columns, currentUser);
  }

  private mapAvailableFilters(
    pageResult: Column.DataViewColumnGallerySchema | undefined,
    domainResult: Column.DataViewDomainColumnsSchema | undefined,
    currentUser?: IUser,
  ): Filter.DataViewFilter[] {
    const filters: Filter.DataViewFilter[] = [];
    const pageColumns = pageResult?.columns.filter(column => !column.hideFilter) ?? [];
    const pageColumnsIds = pageColumns.map(column => column.id) ?? [];
    const pageColumnsIdsWithRequiredFilters = pageColumns.filter(column => column.requiredFilter).map(column => column.id) ?? [];
    for (const [id, column] of Object.entries(domainResult?.columns ?? {})) {
      if (column.filter && this.userHasAccessToItem(column, currentUser) && this.userHasAccessToItem(column.filter, currentUser)) {
        if (
          id === 'claimFlagsList' &&
          !UserService.isCurrentUserSuperAdmin() &&
          !this.featureFlagsService.isOn('spotcheck-workbench-flags')
        )
          continue;
        filters.push({
          ...column,
          id,
          categoryId: column.categoryId ?? '',
          display: column.filter.display ?? column.display,
          field: column.filter.field ?? column.field,
          options: column.filter.options,
          maxNumOfOptions: column.filter.maxNumOfOptions,
          allowSelectAll: column.filter.allowSelectAll,
          multiple: column.filter.multiple,
          allowedTypes: column.filter.allowedTypes,
          caseInsensitive: column.filter.caseInsensitive,
          required: pageColumnsIdsWithRequiredFilters.includes(id),
        });
      }
    }
    return pageColumnsIds.length ? filters.filter(item => pageColumnsIds.includes(item.id)) : filters;
  }

  getColumnCategories(page: string, currentUser?: IUser): Observable<Column.DataViewColumnGalleryCategory[]> {
    return this.getPageAndDomainDefinitions(page).pipe(
      map(({ domainResult }) =>
        this.mapAvailableItems(
          Object.entries(domainResult?.columnCategories ?? {}).map(([id, category]) => ({ ...category, id })),
          currentUser,
        ),
      ),
    );
  }

  getAvailableColumns(page: string, currentUser?: IUser): Observable<Column.DataViewColumn[]> {
    return this.getPageAndDomainDefinitions(page).pipe(
      map(({ pageResult, domainResult }) => this.mapAvailableColumns(pageResult, domainResult, currentUser)),
    );
  }

  getAvailableFilters(page: string, currentUser?: IUser): Observable<Filter.DataViewFilter[]> {
    return this.getPageAndDomainDefinitions(page).pipe(
      map(({ pageResult, domainResult }) => this.mapAvailableFilters(pageResult, domainResult, currentUser)),
    );
  }

  getDataViewPageConfig(page: string, currentUser: IUser): Observable<PageConfig.DataViewPageConfig> {
    return forkJoin({
      userViewDefinitions: this.settingsStore.getSettings<PageConfig.DataViewPageConfigSchema>(
        `user-username:${currentUser.username}`,
        `dataview3_${page}_views`,
      ),
      definitions: this.getPageAndDomainDefinitions(page),
      defaultViewDefinitons: this.getDataViewDefaultViews(page, currentUser),
    }).pipe(
      map(results => {
        const availableColumns = this.mapAvailableColumns(results.definitions.pageResult, results.definitions.domainResult, currentUser);
        const availableFilters = this.mapAvailableFilters(results.definitions.pageResult, results.definitions.domainResult, currentUser);
        if (results.userViewDefinitions) {
          results.userViewDefinitions = this.updateConfigSchema(results.userViewDefinitions, availableColumns, availableFilters);
        }
        const favouriteColumns = [];
        for (const favouriteColumnId of results.userViewDefinitions?.favouriteColumnIds ?? []) {
          const favouriteColumn = availableColumns.find(availableColumn => availableColumn.id === favouriteColumnId);
          if (favouriteColumn) {
            favouriteColumns.push(favouriteColumn);
          }
        }
        const views = [
          ...this.mapViews(
            results.defaultViewDefinitons,
            results.userViewDefinitions?.pinnedViewIds ?? [],
            results.userViewDefinitions?.unpinnedDefaultViewIds ?? [],
            availableColumns,
            availableFilters,
          ),
          ...this.mapCustomViews(
            results.userViewDefinitions?.views ?? [],
            results.userViewDefinitions?.pinnedViewIds ?? [],
            availableColumns,
            availableFilters,
          ),
        ];
        const orderedViews = results.userViewDefinitions?.pinnedViewIds
          ? this.orderViews(views, results.userViewDefinitions.pinnedViewIds)
          : views;
        return {
          views: orderedViews,
          favouriteColumns,
          pinnedViewIds: results.userViewDefinitions?.pinnedViewIds ?? [],
          unpinnedDefaultViewIds: results.userViewDefinitions?.unpinnedDefaultViewIds ?? [],
          configVersion: results.userViewDefinitions?.configVersion,
          availableFilters,
        };
      }),
    );
  }

  getDataViewDefaultViews(page: string, currentUser: IUser) {
    return this.settingsStore
      .getSettings<View.DefaultView[]>(DATAVIEW_SETTINGS_KEY, `${page}_defaultViews`)
      .pipe(map(result => (result ?? []).filter(defaultView => this.userHasAccessToItem(defaultView, currentUser))));
  }

  private updateConfigSchema(
    config: PageConfig.DataViewPageConfigSchema,
    availableColumns: Column.DataViewColumn[],
    availableFilters: Filter.DataViewFilter[],
  ): PageConfig.DataViewPageConfigSchema {
    const version = config.configVersion ?? '1.0.0';
    if (version < CURRENT_DATAVIEW_VERSION) {
      // This deletes defaultViews from saved views. In v1 of DataViews, we
      // saved defaultViews into user views instead of passing in references.
      if ('savedViews' in config) {
        config.views = (config.savedViews as View.View[])
          .filter(view => 'isCustom' in view && view.isCustom)
          .map(view => ({ ...view, id: view.label }));
      }
      config.views = config.views.map((view: any) => {
        const mappedColumns = [];
        const appliedColumnFields: any[] =
          (view as any).columns?.flatMap((column: any) => [
            ...(column.type === 'genericDynamic' ? (column?.appliedSettings?.displayFields ?? [column.field]) : [column.field]),
          ]) ?? [];
        for (const appliedColumn of appliedColumnFields) {
          const mappedColumn = availableColumns.find(availableColumn => availableColumn.field === appliedColumn);
          if (mappedColumn) {
            mappedColumns.push({
              id: mappedColumn.id,
              width: appliedColumn.width,
            });
          }
        }

        const mappedFilters: Filter.AppliedDataViewFilterSchema[] = [];
        view.appliedFilters?.forEach((appliedFilter: any) => {
          const mappedFilter = availableFilters.find(availableFilter => availableFilter.field === appliedFilter.field);
          if (mappedFilter) {
            mappedFilters.push({
              id: mappedFilter.id,
              value: appliedFilter.value,
              type: appliedFilter.type,
              timezone: appliedFilter.timezone,
            });
          }
        });

        return {
          id: view.id,
          label: view.label,
          appliedColumns: mappedColumns,
          appliedFilters: mappedFilters,
          isCustom: view.isCustom,
          isPinned: view.isPinned,
          mapConfig: DEFAULT_MAP_CONFIG,
        };
      });
    }
    return {
      ...config,
      configVersion: CURRENT_DATAVIEW_VERSION,
    };
  }

  setDataViewPageConfig(page: string, currentUser: IUser, config: PageConfig.DataViewPageConfig): Observable<object | undefined> {
    const sharedViews = config.views.filter(
      view =>
        (!('isCustom' in view) || !view.isCustom) &&
        'ownerUsername' in view &&
        view.ownerUsername === this.userService.getCurrentUser().username,
    );
    return this.getDataViewDefaultViews(page, currentUser).pipe(
      mergeMap(defaultViews => {
        if (this.userService.isSuperAdmin()) {
          for (const sharedView of sharedViews) {
            const existingDefaultViewId = defaultViews.findIndex(defaultView => defaultView.id === sharedView.id);
            if (existingDefaultViewId !== -1) {
              defaultViews[existingDefaultViewId] = { ...sharedView };
            } else {
              defaultViews.push({ ...sharedView, isPinned: false });
            }
            config.views = config.views.filter(view => view.id !== sharedView.id);
          }
          defaultViews = defaultViews.filter(
            defaultView =>
              !('ownerUsername' in defaultView) ||
              !defaultView.ownerUsername ||
              defaultView.ownerUsername !== currentUser.username ||
              sharedViews.findIndex(sharedView => sharedView.id === defaultView.id) !== -1,
          );
        }
        return forkJoin({
          userConfig: this.saveSetting<PageConfig.DataViewPageConfigSchema>(
            `user-username:${currentUser.username}`,
            `dataview3_${page}_views`,
            this.simplifyConfigForSaving(config),
          ),
          defaultViewsConfig: this.saveSetting<View.DefaultView[]>('dataview3', `${page}_defaultViews`, defaultViews),
        }).pipe(map(results => results.userConfig));
      }),
    );
  }

  private saveSetting<T>(subject: string, attribute: string, value: T): Observable<T | Error> {
    // The store keeps what the API received, so filter values holding dates are cached in their serialized form.
    const storedValue = JSON.parse(JSON.stringify(value));
    return this.settingsService
      .saveSettings<T>(subject, attribute, value)
      .pipe(mergeMap(result => from(this.settingsStore.set(`${subject}#${attribute}`, storedValue)).pipe(map(() => result))));
  }

  private simplifyConfigForSaving(config: PageConfig.DataViewPageConfig): PageConfig.DataViewPageConfigSchema {
    const simplifiedViews = config.views
      .filter(view => 'isCustom' in view && view.isCustom)
      .map(view => {
        const simplifiedColumns = view.appliedColumns.map(column => ({
          id: column.id,
          width: column.width,
        }));
        const simplifiedFilters = view.appliedFilters.map(filter => ({
          id: filter.id,
          value: filter.value,
          type: filter.type,
          timezone: filter.timezone,
        }));
        return {
          id: view.id,
          appliedColumns: simplifiedColumns,
          appliedFilters: simplifiedFilters,
          mapConfig: view.mapConfig,
          label: view.label,
        };
      });
    return {
      configVersion: CURRENT_DATAVIEW_VERSION,
      pinnedViewIds: config.pinnedViewIds,
      unpinnedDefaultViewIds: config.unpinnedDefaultViewIds,
      views: simplifiedViews,
      favouriteColumnIds: config.favouriteColumns.map(favouriteColumn => favouriteColumn.id),
    };
  }

  private userHasAccessToItem(item: DataViewItemWithAccess, currentUser?: IUser) {
    const hasAccessToAllTenants = this.userService.hasAllTenantsAccess();
    const roleHasAccess =
      (!item.roleWhitelist || (currentUser?.role && item.roleWhitelist.includes(currentUser?.role as UserRole))) &&
      (!item.roleBlacklist || (currentUser?.role && !item.roleBlacklist.includes(currentUser?.role as UserRole))) &&
      (!item.tenantWhitelist || hasAccessToAllTenants || intersection(item.tenantWhitelist, currentUser?.tenants ?? []).length > 0) &&
      (!item.tenantBlacklist || hasAccessToAllTenants || difference(item.tenantBlacklist, currentUser?.tenants ?? []).length > 0);
    const userHasRelevantTenantsAccess =
      !item.forTenantsWhitelist ||
      [UserRole.Administrator, 'FinanceAdmin'].includes(currentUser?.role as UserRole) ||
      (currentUser?.tenants && item.forTenantsWhitelist.every(tenant => currentUser?.tenants?.includes(tenant)));

    return roleHasAccess && userHasRelevantTenantsAccess;
  }

  private mapAvailableItems<T extends DataViewItemWithAccess>(items: T[], currentUser?: IUser) {
    const availableItems: T[] = [];
    for (const item of items) {
      if (this.userHasAccessToItem(item, currentUser)) {
        availableItems.push(item);
      }
    }
    return availableItems;
  }

  private mapCustomViews(
    views: View.ViewSchema[],
    pinnedViewIds: string[],
    availableColumns: Column.DataViewColumn[],
    availableFilters: Filter.DataViewFilter[],
  ): View.UserView[] {
    return this.mapViews(views, pinnedViewIds, [], availableColumns, availableFilters).map(
      view => ({ ...view, isCustom: true }) as View.UserView,
    );
  }

  private orderViews(views: Array<View.UserView | View.DefaultView>, pinnedViewIds: string[]) {
    const orderedViews: Array<View.UserView | View.DefaultView> = [];
    const mandatoryDefaultViews: View.DefaultView[] = [];
    for (const pinnedViewId of pinnedViewIds) {
      if (orderedViews.some(view => view.id === pinnedViewId || (!view.id && !pinnedViewId))) {
        continue;
      }
      const currentView = views.find(view => view.id === pinnedViewId || (!view.id && !pinnedViewId));
      if (currentView) {
        orderedViews.push(currentView);
      }
    }
    for (const defaultPinnedView of views.filter(view => !view.isCustom)) {
      if (orderedViews.findIndex(view => view.id === defaultPinnedView.id) === -1) {
        mandatoryDefaultViews.push(defaultPinnedView);
      }
    }
    return [...mandatoryDefaultViews, ...orderedViews];
  }

  private mapViews(
    views: View.ViewSchema[],
    pinnedViewIds: string[],
    unpinnedDefaultViewIds: string[],
    availableColumns: Column.DataViewColumn[],
    availableFilters: Filter.DataViewFilter[],
  ): Array<View.UserView | View.DefaultView> {
    const mappedViews = [];
    for (const view of views) {
      const appliedColumns = [];
      for (const column of view.appliedColumns ?? []) {
        const availableColumn = availableColumns.find(availableColumn => availableColumn.id === column.id);
        if (availableColumn) {
          appliedColumns.push({
            ...availableColumn,
            ...column,
          });
        }
      }

      const appliedFilters = [];
      for (const filter of view.appliedFilters) {
        const availableFilter = availableFilters.find(availableFilter => availableFilter.id === filter.id);
        if (availableFilter) {
          const appliedFilter = {
            ...availableFilter,
            type: filter.type ?? availableFilter.allowedTypes[0],
            value: filter.value,
            timezone: filter.timezone,
          };
          if (appliedFilter.value !== null && typeof appliedFilter.value === 'object' && !Array.isArray(appliedFilter.value)) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { _preset, _presetDisplayValue, ...cleanValue } = appliedFilter.value as {
              _preset: unknown;
              _presetDisplayValue: unknown;
              restOfValue: unknown;
            };
            appliedFilter.value = {
              ...cleanValue,
            };
          }
          appliedFilters.push(appliedFilter);
        }
      }

      const isPinned = (view.isPinned || pinnedViewIds.includes(view.id)) && !unpinnedDefaultViewIds.includes(view.id);

      mappedViews.push({
        ...view,
        mapConfig: 'mapConfig' in view ? view.mapConfig : DEFAULT_MAP_CONFIG,
        isPinned,
        appliedColumns,
        appliedFilters,
      });
    }
    return mappedViews;
  }
}
